import { useEffect, useMemo, useState } from "react";
import { Award, Clock, GraduationCap } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SEO } from "@/components/SEO";
import { PageHero, Loading, VerifyBadge } from "@/components/shared";

export default function Coaches() {
  const [coaches, setCoaches] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => { api.get("/public/coaches").then((r) => setCoaches(r.data)); }, []);

  const groups = useMemo(() => {
    if (!coaches) return [];
    const set = new Set(coaches.map((c) => c.age_group).filter(Boolean));
    return ["all", ...Array.from(set)];
  }, [coaches]);

  const shown = coaches?.filter((c) => filter === "all" || c.age_group === filter) || [];
  const initials = (n) => n.split(" ").map((x) => x[0]).slice(0, 2).join("");

  return (
    <>
      <SEO title="Our Coaches | Brackenfell Junior Rugby Club" description="Meet the BJRC coaching team — qualified, experienced coaches dedicated to developing young rugby players." />
      <PageHero title="Our Coaches" subtitle="Meet the dedicated coaching team guiding our young players." />
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        {coaches === null ? <Loading /> : (
          <>
            {groups.length > 1 && (
              <div className="mb-10 flex flex-wrap gap-2">
                {groups.map((g) => (
                  <button key={g} onClick={() => setFilter(g)} data-testid={`coach-filter-${g.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}
                    className={`rounded-full px-5 py-2 text-sm font-bold uppercase tracking-wide transition-all ${filter === g ? "bg-[#0B2A4A] text-white" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-[#1E63D6]"}`}>
                    {g === "all" ? "All Coaches" : g}
                  </button>
                ))}
              </div>
            )}
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {shown.map((c) => (
                <button key={c.id} onClick={() => setSelected(c)} data-testid={`coach-card-${c.id}`}
                  className="group overflow-hidden rounded-2xl border border-slate-200 bg-white text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                  <div className="relative aspect-[3/4] bg-gradient-to-b from-[#0B2A4A] to-[#1E63D6]">
                    {c.photo_url ? <img src={mediaUrl(c.photo_url)} alt={c.name} className="h-full w-full object-cover object-top" /> : (
                      <div className="flex h-full items-center justify-center font-heading text-5xl font-black text-white/25">{initials(c.name)}</div>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-lg font-bold uppercase text-[#0B2A4A]">{c.name}</h3>
                    <p className="text-sm text-slate-500">{c.role}{c.age_group ? ` · ${c.age_group}` : ""}</p>
                    {c.qualifications && (
                      <p className="mt-3 flex gap-2 text-xs text-slate-600" data-testid={`coach-qual-${c.id}`}>
                        <GraduationCap className="h-4 w-4 shrink-0 text-[#1E63D6]" />
                        <span>{c.qualifications}</span>
                      </p>
                    )}
                    {c.years_experience && (
                      <p className="mt-2 flex gap-2 text-xs text-slate-600" data-testid={`coach-exp-${c.id}`}>
                        <Clock className="h-4 w-4 shrink-0 text-[#1E63D6]" />
                        <span>{c.years_experience}</span>
                      </p>
                    )}
                    {c.migration_status !== "verified" && <div className="mt-3"><VerifyBadge /></div>}
                  </div>
                </button>
              ))}
            </div>
            {shown.length === 0 && <p className="py-16 text-center text-slate-500">No coaches in this group yet.</p>}
          </>
        )}
      </section>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          {selected && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#0B2A4A]">
                    {selected.photo_url ? <img src={mediaUrl(selected.photo_url)} alt={selected.name} className="h-full w-full object-cover" /> : (
                      <div className="flex h-full items-center justify-center font-heading text-2xl font-black text-white/40">{initials(selected.name)}</div>
                    )}
                  </div>
                  <div>
                    <DialogTitle className="font-heading text-2xl uppercase text-[#0B2A4A]">{selected.name}</DialogTitle>
                    <p className="text-sm text-slate-500">{selected.role}{selected.age_group ? ` · ${selected.age_group}` : ""}</p>
                  </div>
                </div>
                <DialogDescription className="sr-only">Coach profile details for {selected.name}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-700">
                {selected.qualifications && <p className="flex gap-2"><GraduationCap className="h-5 w-5 shrink-0 text-[#1E63D6]" /><span><b>Qualifications:</b> {selected.qualifications}</span></p>}
                {selected.years_experience && <p className="flex gap-2"><Clock className="h-5 w-5 shrink-0 text-[#1E63D6]" /><span><b>Experience:</b> {selected.years_experience}</span></p>}
                {selected.playing_experience && <p className="flex gap-2"><Award className="h-5 w-5 shrink-0 text-[#1E63D6]" /><span><b>Playing:</b> {selected.playing_experience}</span></p>}
                {selected.biography && <p className="pt-2">{selected.biography}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
